import {
  FPNDetailsHierarchy,
  ProductLineHierarchy,
  RPNDetailsHierarchy,
} from 'src/types/productLines/productHierarchy.types';

/**
 * Type representing hierarchy levels in the product structure
 */
export type HierarchyLevel = 'PRODUCT_LINE' | 'RPN' | 'FPN';

/**
 * Extended Product Line type with added hierarchy metadata fields
 * These fields are derived client-side for Ag-Grid tree data functionality
 * Uses Omit to properly override the children type
 */
export interface ProductLineWithMeta extends Omit<ProductLineHierarchy, 'children'> {
  id: string;
  hierarchy_level: HierarchyLevel;
  hierarchy_name: string;
  children: RPNDetailsWithMeta[];
}

/**
 * Extended RPN type with added hierarchy metadata fields
 * Includes aggregated FSD (First Ship Date) data from child FPNs
 * Uses Omit to properly override the children type and add new FSD fields
 *
 * Note: first_street_date is defined here with the required modifier
 * to override the optional field from RPNDetailsHierarchy, since this value
 * is always computed during metadata enrichment (null if no FPN children).
 */
export interface RPNDetailsWithMeta extends Omit<RPNDetailsHierarchy, 'children' | 'first_street_date'> {
  id: string;
  rpn_display_id: string;
  hierarchy_level: HierarchyLevel;
  hierarchy_name: string;
  product_line_id: number; // Parent product line ID for navigation
  product_code: string; // Parent product code for URL-based navigation
  children: FPNDetailsWithMeta[];
  // Aggregated FSD fields from child FPNs (required - always computed during enrichment)
  first_street_date: string | null; // Minimum date among all FPN children
  fsd_countries: string[] | null; // Distinct countries from all FPN children
}

/**
 * Extended FPN type with added hierarchy metadata fields
 * Includes parsed FSD countries array from JSON string
 * Uses Omit to properly override the fsd_countries type
 */
export interface FPNDetailsWithMeta extends Omit<FPNDetailsHierarchy, 'fsd_countries'> {
  id: string;
  hierarchy_level: HierarchyLevel;
  hierarchy_name: string;
  product_line_id: number; // Parent product line ID for navigation
  product_code: string; // Parent product code for URL-based navigation
  rpn_id: number; // Parent RPN ID for navigation
  rpn_display_id: string;
  fpn_display_id: string;
  fsd_countries: string[] | null; // Parsed from JSON string to array
}

/**
 * Union type for nodes with metadata
 */
export type ProductHierarchyNodeWithMeta = ProductLineWithMeta | RPNDetailsWithMeta | FPNDetailsWithMeta;

/**
 * Adds hierarchy metadata fields to FPN details
 * These fields are required for Ag-Grid tree data and navigation functionality
 * Note: FSD countries are already parsed to array in the Redux slice
 *
 * @param fpn - FPN details from backend API (already transformed)
 * @param productLineId - Parent product line ID
 * @param productCode - Parent product code for URL-based navigation
 * @param rpnId - Parent RPN ID
 * @returns FPN details with added id, hierarchy_level, hierarchy_name fields
 */
const addFPNMetadata = (
  fpn: FPNDetailsHierarchy,
  productLineId: number,
  productCode: string,
  rpnId: number,
  rpnDisplayId: string
): FPNDetailsWithMeta => {
  return {
    ...fpn,
    id: `FPN-${productLineId}-${rpnId}-${fpn.fpn_id}`,
    hierarchy_level: 'FPN',
    hierarchy_name: fpn.fpn_alias,
    product_line_id: productLineId,
    product_code: productCode,
    rpn_id: rpnId,
    rpn_display_id: rpnDisplayId,

    fpn_id: fpn.fpn_id,
    fpn_display_id: fpn.fpn_display_id,
    // fsd_countries is already parsed as string[] | null in the Redux slice
    fsd_countries: fpn.fsd_countries as string[] | null,
  };
};

/**
 * Adds hierarchy metadata fields to RPN details and recursively processes FPN children
 * These fields are required for Ag-Grid tree data and navigation functionality
 * Note: FSD aggregation is already done in the Redux slice
 *
 * @param rpn - RPN details from backend API (already transformed with FSD aggregation)
 * @param productLineId - Parent product line ID
 * @param productCode - Parent product code for URL-based navigation
 * @returns RPN details with added id, hierarchy_level, hierarchy_name fields and processed children
 */
const addRPNMetadata = (rpn: RPNDetailsHierarchy, productLineId: number, productCode: string): RPNDetailsWithMeta => {
  const childrenWithMeta = rpn.children.map((fpn) =>
    addFPNMetadata(fpn, productLineId, productCode, rpn.rpn_id, rpn.rpn_display_id)
  );

  return {
    ...rpn,
    id: `RPN-${productLineId}-${rpn.rpn_id}`,
    rpn_display_id: rpn.rpn_display_id,
    hierarchy_level: 'RPN',
    hierarchy_name: rpn.rpn_alias,
    product_line_id: productLineId,
    product_code: productCode,
    children: childrenWithMeta,
    // FSD fields are already aggregated in the Redux slice
    first_street_date: rpn.first_street_date ?? null,
    fsd_countries: rpn.fsd_countries ?? null,
  };
};

/**
 * Adds hierarchy metadata fields to product line details and recursively processes RPN and FPN children
 * These fields are required for Ag-Grid tree data and navigation functionality
 *
 * This utility adds three derived fields to each node in the hierarchy:
 * - id: Unique identifier for Ag-Grid tree data (format: "{level}-{product_line_id}[-{rpn_id}][-{fpn_id}]")
 * - hierarchy_level: Node type in hierarchy ("PRODUCT_LINE", "RPN", or "FPN")
 * - hierarchy_name: Display name (uses product_line, rpn_alias, or fpn_alias respectively)
 *
 * @param productLine - Product line details from backend API
 * @returns Product line details with added metadata fields for all nodes in the hierarchy
 *
 * @example
 * ```typescript
 * const rawData = await fetchProductHierarchyAPI();
 * const dataWithMetadata = rawData.map(addProductHierarchyMetadata);
 * ```
 */
export const addProductHierarchyMetadata = (productLine: ProductLineHierarchy): ProductLineWithMeta => {
  const childrenWithMeta = productLine.children.map((rpn) =>
    addRPNMetadata(rpn, productLine.product_line_id, productLine.product_code)
  );

  return {
    ...productLine,
    id: `PL-${productLine.product_line_id}`,
    hierarchy_level: 'PRODUCT_LINE',
    hierarchy_name: productLine.product_line,
    children: childrenWithMeta,
  };
};

/**
 * Transforms an array of product line hierarchy data by adding metadata fields to all nodes
 * This is a convenience wrapper around addProductHierarchyMetadata for processing arrays
 *
 * @param data - Array of product line hierarchy details from backend API
 * @returns Array of product line hierarchy details with added metadata fields
 *
 * @example
 * ```typescript
 * const rawData = await fetchProductHierarchyAPI();
 * const enrichedData = enrichProductHierarchyData(rawData);
 * ```
 */
export const enrichProductHierarchyData = (data: ProductLineHierarchy[]): ProductLineWithMeta[] => {
  return data.map(addProductHierarchyMetadata);
};

/**
 * Builds a lookup map from product_line_id to sorted sibling RPN aliases.
 *
 * Used for Program Predecessor dropdown which shows FIRST_GENERATION_LABEL + sibling RPNs
 * (excluding the current row's RPN in the calling component).
 *
 * This utility is shared between:
 * - Bulk Edit ProductHierarchyTable (AG Grid cell editor)
 * - EditableOverviewSection / useEditableOverview (Cloudscape Select)
 *
 * @param productLines - Array of product lines with RPN children (must have product_line_id and children with rpn_alias)
 * @returns Map from product_line_id to sorted array of RPN alias strings
 */
export const buildSiblingRpnAliasMap = (
  productLines: ReadonlyArray<{ product_line_id: number; children: ReadonlyArray<{ rpn_alias: string }> }>
): Map<number, string[]> => {
  const map = new Map<number, string[]>();
  for (const pl of productLines) {
    const rpnAliases = pl.children
      .map((rpn) => rpn.rpn_alias)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    map.set(pl.product_line_id, rpnAliases);
  }
  return map;
};
